import { useState, useEffect, type CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopNav from './TopNav';
import { useAuthStore } from '@/store/auth.store';
import { useDeploymentStore } from '@/store/deployment.store';
import { useUserNameStore } from '@/store/userName.store';
import { iamApi } from '@/api/iam.api';
import { getLoginPath } from '@/pages/auth/loginMode';
import { mergeCurrentProfileUserName } from '@/lib/userNameMap';
import { redirectToOidcLogout } from '@/pages/auth/oidc';
import { useAccountProfile } from './hooks/useAccountProfile';
import './platform-layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { token, logout } = useAuthStore();
  const loginMode = useDeploymentStore((state) => state.profile.login.mode);
  const { loaded, setNameMap, mergeNameMap, reset } = useUserNameStore();
  const { data: accountProfile } = useAccountProfile();
  const navigate = useNavigate();
  const location = useLocation();
  // 访问控制模式由路由派生，刷新 / 直达 /iam/* 时侧栏仍显示 IAM 菜单。
  const accessControl = location.pathname.startsWith('/iam');

  // 登录后独立拉取一次批量映射，不依赖当前用户 profile 的加载时序。
  useEffect(() => {
    if (!token || loaded) {
      return;
    }
    iamApi
      .listUsersName()
      .then((res) => setNameMap(res.userNames))
      .catch(() => {
        // 批量映射失败也结束本轮加载，当前用户由下方 profile effect 补充。
        setNameMap({});
      });
  }, [token, loaded, setNameMap]);

  // 以当前登录 profile.user 校正 IAM 子用户名称。store 会保留该权威映射，
  // 即使 StrictMode 触发的重复批量请求稍后返回，也不会将其覆盖。
  useEffect(() => {
    if (!accountProfile) {
      return;
    }
    mergeNameMap(mergeCurrentProfileUserName({}, accountProfile));
  }, [accountProfile, mergeNameMap]);

  const handleLogout = () => {
    // OIDC 先占用退出导航；旧 SAML 流程只清理本地态并返回原 SSO 页面。
    if (loginMode === 'oidc') {
    // 先让登出占用本页唯一的 OIDC 导航，再清空所有本地登录态。
    redirectToOidcLogout(token || '');
    }
    reset();
    logout();
    if (loginMode !== 'oidc') {
      navigate(getLoginPath(loginMode), { replace: true });
    }
  };

  return (
    <div
      className={`platform-scope platform-app ${collapsed ? 'is-collapsed' : ''}`}
      style={{ '--sidebar-width': collapsed ? '56px' : '200px' } as CSSProperties}
    >
      <TopNav
        collapsed={collapsed}
        onLogout={handleLogout}
        onEnterAccessControl={() => navigate('/iam/users')}
      />
      <Sidebar
        collapsed={collapsed}
        onCollapseChange={setCollapsed}
        accessControl={accessControl}
        onExitAccessControl={() => navigate('/dashboard')}
      />
      <main className="main">{children}</main>
    </div>
  );
}
